import { useContext, useEffect, useState } from "react";
import { useWarehouse } from "../Hooks/useWarehouse.js";
import { useVendor } from "../../VendorMaster/Hooks/useVendor";
import { STOCK_MODAL_MODES } from "../constants.js";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { ArrowLeft, Trash2 } from "lucide-react";
import { EDIT, VENDOR_LIST, VIEW } from "../../../routes.js";
import {
  AccessDenied,
  GenericTable,
  RoleValidator,
  SearchableDropdown,
  ProductCategoryFilter,
  Spinner,
} from "../../../Components/Common/index.js";
import {
  getVGNStatusColor,
  MATERIAL_COLUMNS_VGN,
  MATERIAL_LABELS_VGN,
  VGN_STATUS,
  VGN_TOAST_MESSAGES,
} from "./constants.js";
import { toProperCase } from "../../../Utils/index.js";
import { extractFileName } from "../../../Utils/extractFileName.js";
import { UserContext } from "../../../Context/index.js";
import { useVendorGoodsNote } from "../Hooks/useVendorGoodsNote.js";
import { ROLES } from "../../../Constants/Strings.js";
import { FiDownload, FiTrash2 } from "react-icons/fi";
import { downloadUtil } from "../../../Utils/DownloadUtils.js";
import {
  PESubmitButtons,
  PMApprovalButtons,
} from "./Components/SubmitButtonsApproval.js";

const VendorGoodsNoteActions = () => {
  const [loading, setLoading] = useState(true);
  const { fetchVGNById, newVendorGoodsNote, updateVGNDetails, deleteVGNFile } =
    useVendorGoodsNote();
  const { fetchAllVendors } = useVendor();
  const { fetchWarehouses } = useWarehouse();
  const [mode, setMode] = useState(STOCK_MODAL_MODES.ADD);
  const [warehouse, setWarehouse] = useState("");
  const [vendor, setVendor] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [materialFile, setMaterialFile] = useState(null);
  const [materialUrl, setMaterialUrl] = useState(null);
  const [status, setStatus] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [vendorPhone, setVendorPhone] = useState("");
  const [formData, setFormData] = useState({
    notes: "",
  });
  const location = useLocation();
  const [showUploadPopup, setShowUploadPopup] = useState(false);
  const [materials, setMaterials] = useState([]);
  const navigate = useNavigate();
  const param = useParams();
  const { user } = useContext(UserContext);
  const accessedRoles = [
    ROLES.INVENTORY_ADMIN,
    ROLES.INVENTORY_INCHARGE,
    ROLES.BILLING_INCHARGE,
    ROLES.PROCUREMENT_INCHARGE,
    ROLES.PROJECT_ENGINEER,
    ROLES.PROJECT_MANAGER,
  ].includes(user?.role);
  const restrictCreatePage =
    user.role !== ROLES.PROJECT_ENGINEER && mode === STOCK_MODAL_MODES.ADD;

  const isDisabled = mode !== STOCK_MODAL_MODES.ADD;

  const resetForm = () => {
    setFormData({ notes: "",});
    setWarehouseId("");
    setWarehouse("");
    setVendorId("");
    setVendor("");
    setVendorPhone("");
    setMaterials([]);
    setStatus("");
  };

  const removeMaterial = (product) => {
    setMaterials((prev) => prev.filter((item) => item.id !== product.id));
    toast.success(`${product.product_name} removed form the list`);
  };

  const handleAddMaterial = (material) => {
    if (!material) return;
    setMaterials((prev) => {
      // Check if material with same id or name already exists
      const exists = prev.some(
        (item) =>
          item.id === material.id || item.product_name === material.product_name
      );
      if (exists) {
        toast.error(`${material.product_name} already added`);
        return prev;
      } else {
        toast.success(`${material.product_name} added`);
      }
      // skip adding duplicate
      return [...prev, { ...material, product: material.id, quantity: 1 }];
    });
  };

  const renderField = (column, row) => {
    switch (column) {
      case "product_name":
        return (
          <div
            className="flex flex-col"
            disabled={mode === STOCK_MODAL_MODES.VIEW}
          >
            <span className="font-semibold">{row.product_name}</span>
            <span className="text-gray-500 font-normal">
              {row.specification}
            </span>
            <span className="text-gray-800 font-normal italic">
              ({row.UOM_name})
            </span>
          </div>
        );
      case "note":
        return (
          <div className="border-b-2 border-gray-400">
            <input
              type="text"
              value={row.note ?? ""}
              placeholder="Enter note..."
              onChange={(e) =>
                setMaterials((prev) =>
                  prev.map((m) =>
                    m.id === row.id ? { ...m, note: e.target.value } : m
                  )
                )
              }
              disabled={mode === STOCK_MODAL_MODES.VIEW}
              className="w-32 h-7 bg-inherit border-none focus:ring-0  text-start"
            />
          </div>
        );
      case "quantity":
        return (
          <div className="border-b-2 border-gray-400 w-14">
            <input
              type="number"
              min="1"
              value={Number(row.quantity ?? 0)}
              onChange={(e) =>
                setMaterials((prev) =>
                  prev.map((m) =>
                    m.id === row.id ? { ...m, quantity: e.target.value } : m
                  )
                )
              }
              disabled={mode === STOCK_MODAL_MODES.VIEW}
              className="w-18 h-7 bg-inherit border-none focus:ring-0 text-start"
            />
          </div>
        );
      case "actions":
        return (
          <>
            <RoleValidator allowedRoles={ROLES.PROJECT_ENGINEER}>
              <button
                className=" hover:underline ml-2"
                onClick={() => removeMaterial(row)}
              >
                <Trash2 className="h-5 w-5 text-red-400" />
              </button>
            </RoleValidator>
          </>
        );
      default:
        return row[column] || "N/A";
    }
  };

  const handleUploadFile = async () => {
    if (!materialFile) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("material_photo", materialFile);
      const res = await updateVGNDetails(param.instanceId, formData, true);
      if (res) {
        setMaterialUrl(res.material_photo);
        setMaterialFile(null);
      }
    } catch (error) {
      toast.error(VGN_TOAST_MESSAGES.FILE_UPLOAD_FAILED);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (fileUrl) => {
    if (!window.confirm("Delete this file?")) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("material_photo", fileUrl);
      await deleteVGNFile(
        param.instanceId,
        formData,
        true // optional flag for formData
      );
      setMaterialUrl(null);
    } catch (error) {
      toast.error(VGN_TOAST_MESSAGES.FILE_DELETE_FAILED);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (fileUrl) => {
    setLoading(true);
    try {
      await downloadUtil(fileUrl);
      toast.success(VGN_TOAST_MESSAGES.FILE_DOWNLOAD_SUCCESS);
    } catch (error) {
      toast.error(VGN_TOAST_MESSAGES.FILE_DOWNLOAD_FAILED);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (submitType) => {
    const missingFields = [];
    if (!warehouseId) missingFields.push("Requesting warehouse");
    if (!formData.notes) missingFields.push("Notes");
    if (materials.length === 0) missingFields.push("at least one material");

    if (missingFields.length > 0) {
      toast.error(`Please provide: ${missingFields.join(", ")}`);
      return;
    }

    const payload = {
      site_warehouse: warehouseId,
      status: submitType,
      notes: formData.notes,
      materials,
      vendor_mobile_no: vendorPhone,
      vendor: vendorId,
    };
    var data = null;
    if (mode === STOCK_MODAL_MODES.ADD) {
      data = await newVendorGoodsNote(payload);
    } else {
      data = await updateVGNDetails(param.instanceId, payload);
      setMaterials(data.materials);
      setFormData({
        notes: data.notes,
      });
      setWarehouse(data.warehouse_name);
      setWarehouseId(data.site_warehouse);
      setVendor(data.vendor);
      setVendorId(data.vendorId);
      setVendorPhone(data.vendorPhone)
      setStatus(data.status);
    }
    navigate(VENDOR_LIST.replace(":instanceId", data.id));
  };

  const getVendorGoodsNoteData = async () => {
    setLoading(true);
    try {
      const data = await fetchVGNById(param.instanceId);

      if (data) {
        setFormData({
          notes: data.notes || "",
        });
        setMaterialUrl(data.material_photo || null);
        setWarehouseId(data.site_warehouse || "");
        setWarehouse(data.warehouse_name || "");
        setVendorId(data.vendorId || "");
        setVendor(data.vendor_name || "-");
        setVendorPhone(data.vendor_mobile_no || "");
        setMaterials(data.materials || []);
        setStatus(data.status || "");
      } else {
        resetForm();
      }
    } catch (error) {
      resetForm();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (param.instanceId) {
      getVendorGoodsNoteData();
      if (location.pathname.includes(EDIT)) {
        setMode(STOCK_MODAL_MODES.EDIT);
      } else if (location.pathname.includes(VIEW)) {
        setMode(STOCK_MODAL_MODES.VIEW);
      }
    } else {
      setMode(STOCK_MODAL_MODES.ADD);
      setLoading(false);
    }
  }, [param.instanceId, location.pathname]);

  if (loading)
    return (
      <div className="flex items-center justify-center h-[60vh] pt-10">
        <Spinner size={4} />
      </div>
    );

  if (!accessedRoles || restrictCreatePage) return <AccessDenied />;

  return (
    <div
      className={`min-h-[70vh] ${
        mode === STOCK_MODAL_MODES.EDIT ? "overflow-y-auto max-h-[90vh]" : ""
      }`}
    >
      <div className="flex justify-between items-center mt-2">
        <h2 className="flex gap-2 justify-center items-center text-xl font-semibold text-gray-800 mt-2 px-3 py-2">
          <button
            type="button"
            onClick={() => navigate(VENDOR_LIST)}
            className="text-primary text-center text-sm font-lg"
          >
            <ArrowLeft size={23} absoluteStrokeWidth className="text-primary" />
          </button>
          {mode === STOCK_MODAL_MODES.EDIT
            ? "Edit"
            : mode === STOCK_MODAL_MODES.VIEW
            ? ""
            : "Create"}{" "}
          Vendor Goods Notes
        </h2>
      </div>
      <div className="mx-auto grid grid-cols-1 md:grid-cols-2 md:gap-6 gap-4 px-4 py-2">
        {/* Left Side — Warehouse & Notes */}
        <div className="bg-white md:border-r-2 md:pr-8">
          <h3 className="text-lg font-semibold text-primary mb-2 md:mb-4 flex items-center gap-2">
            <span className="inline-block w-1.5 h-6 bg-primary rounded-full"></span>
            VGN Materials
          </h3>
          <div className="flex flex-col items-center justify-between gap-2 md:gap-4 w-full">
            <div className="w-full">
              <label className="block text-gray-700 font-medium md:text-sm text-xs mb-1">
                {`Requested By (Warehouse)`}{" "}
                <sub className="text-red-500">*</sub>
              </label>
              <SearchableDropdown
                fetchFunction={(param) =>
                  fetchWarehouses({ ...param, is_site: true })
                }
                onSelect={(val) => setWarehouseId(val.id)}
                labelKey="name"
                placeholder={warehouse ? warehouse : "Select Warehouse"}
                disabled={isDisabled}
              />
            </div>
            <div className="flex items-center justify-between gap-4 w-full">
              <div className="w-[50%]">
                <label className="block text-gray-700 font-medium md:text-sm text-xs mb-1">
                  Vendor Name <sub className="text-red-500">*</sub>
                </label>
                <SearchableDropdown
                  fetchFunction={fetchAllVendors}
                  onSelect={(val) => {
                    setVendorId(val.id);
                    setVendorPhone(val.mobile || "");
                  }}
                  labelKey="name"
                  placeholder={vendor ? vendor : "Select Vendor"}
                  disabled={isDisabled}
                />
              </div>
              <div className="w-[50%]">
                <label className="block text-gray-700 font-medium md:text-sm text-xs mb-1">
                  Vendor Phone <sub className="text-red-500">*</sub>
                </label>
                <input
                  type="text"
                  value={vendorPhone}
                  readOnly
                  placeholder="phone number"
                  className="w-full border border-gray-300 bg-gray-100 text-gray-700 rounded-lg px-3 py-2 focus:ring-1 focus:ring-blue-500 focus:outline-none resize-none"
                />
              </div>
            </div>
          </div>
          {/* Notes Field */}
          <div className="md:mb-4">
            <label className="block text-gray-700 font-medium md:text-sm text-xs my-2">
              Notes <sub className="text-red-500">*</sub>
            </label>
            <textarea
              rows="2"
              value={formData.notes}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  notes: e.target.value,
                }))
              }
              placeholder="Write any notes about this return..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-1 focus:ring-blue-500 focus:outline-none resize-none"
              disabled={mode === STOCK_MODAL_MODES.VIEW}
            ></textarea>
          </div>
          <div className="flex flex-col md:flex-row items-start justify-start md:items-center md:justify-between md:gap-4">
            {mode !== STOCK_MODAL_MODES.ADD && status && (
              <div className="flex flex-col">
                <label className="block text-gray-700  font-medium md:text-sm text-xs mb-1">
                  Status
                </label>
                <span
                  className={`py-1.5 px-3 text-sm md:font-medium rounded-lg ${getVGNStatusColor(
                    status
                  )}`}
                >
                  {toProperCase(status)}
                </span>
              </div>
            )}
            {/* Upload File Option for Procurement Incharge */}
            {mode !== STOCK_MODAL_MODES.ADD && (
              <div className="w-[60%]">
                {materialUrl ? (
                  <div>
                    <label className="block text-gray-700 font-medium md:text-sm text-xs my-2">
                      Material Photo Upload
                    </label>
                    <div className="flex items-center justify-between bg-gray-100 px-3 py-1 rounded-lg border">
                      <a
                        href={materialUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline text-sm truncate max-w-[350px]"
                      >
                        {extractFileName(materialUrl)}
                      </a>
                      <div className="flex items-center gap-1">
                        {user.role === ROLES.PROJECT_ENGINEER &&
                          status === VGN_STATUS.DRAFT && (
                            <button
                              onClick={() => handleDelete(materialUrl)}
                              className="text-red-500 text-xs px-1 py-1"
                            >
                              <FiTrash2 size={16} />
                            </button>
                          )}

                        <button
                          onClick={() => handleDownload(materialUrl)}
                          className="text-primary text-xs px-1 py-1"
                        >
                          <FiDownload size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {user.role === ROLES.PROJECT_ENGINEER &&
                      status === VGN_STATUS.DRAFT &&
                      mode === STOCK_MODAL_MODES.EDIT && (
                        <div>
                          <label className="block text-gray-700 font-medium md:text-sm text-xs my-1">
                            Material Photo Upload
                          </label>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => setMaterialFile(e.target.files[0])}
                            className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none"
                          />
                          {materialFile && (
                            <>
                              <RoleValidator
                                allowedRoles={ROLES.PROJECT_ENGINEER}
                              >
                                <button
                                  onClick={handleUploadFile}
                                  className="mt-2 bg-primary text-white px-3 py-1 rounded-lg text-sm hover:bg-blue-700"
                                >
                                  Upload Material Photo
                                </button>
                              </RoleValidator>
                            </>
                          )}
                        </div>
                      )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Side — Selected Materials */}
        <div>
          <h3 className="text-lg font-semibold text-primary mb-2 md:mb-4 flex items-center text-center gap-2">
            <span className="inline-block w-1.5 h-6 bg-primary rounded-full"></span>
            Selected Materials
          </h3>
          {!(mode === STOCK_MODAL_MODES.VIEW) && (
            <>
              <label className="block text-gray-700 font-medium text-xs md:text-sm mb-1">
                Add Product <sub className="text-red-500">*</sub>
              </label>
              <ProductCategoryFilter
                onProductSelect={handleAddMaterial}
              />
              <label className="block text-gray-700 font-medium text-xs md:text-sm my-2">
                Selected Product List
              </label>
            </>
          )}

          {materials.length === 0 ? (
            <div className="text-gray-600 text-center py-11 border rounded-lg">
              No materials selected yet.
            </div>
          ) : (
            <div
              className={`overflow-x-auto ${
                mode === STOCK_MODAL_MODES.ADD ? "h-[26vh]" : "h-[23vh]"
              } md:h-[40vh] overflow-y-auto`}
            >
              {loading ? (
                <div className="h-full w-full flex justify-center items-center">
                  <div className=" animate-spin border-t border-blue-600"></div>
                </div>
              ) : (
                <GenericTable
                  tableColumns={
                    mode === STOCK_MODAL_MODES.VIEW
                      ? MATERIAL_COLUMNS_VGN.filter((col) => col !== "actions")
                      : MATERIAL_COLUMNS_VGN
                  }
                  tableRows={materials}
                  columnLabels={MATERIAL_LABELS_VGN}
                  renderField={renderField}
                  enableMobileCardView={false}
                />
              )}
            </div>
          )}
          {/* Action Buttons Section */}
          <div className="mt-3">
            {((status === VGN_STATUS.DRAFT &&
              mode === STOCK_MODAL_MODES.EDIT) ||
              mode === STOCK_MODAL_MODES.ADD) &&
              user.role === ROLES.PROJECT_ENGINEER && (
                <PESubmitButtons handleSubmit={handleSubmit} />
              )}

            {status === VGN_STATUS.PENDING_FOR_APPROVAL &&
              mode === STOCK_MODAL_MODES.VIEW &&
              user.role === ROLES.PROJECT_MANAGER && (
                <PMApprovalButtons handleSubmit={handleSubmit} />
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorGoodsNoteActions;
